import { Box } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export interface PieItem {
  zone_id?: number;
  name: string;
  y: number;
}

interface Props {
  locationData: PieItem[] | [];
  sectionData: PieItem[] | [];
  location?: string;
  onChangeFilter: (v: string, filterType: string) => void;
}

export interface PieRef {
  updatePieChart?: Function;
}

const COLORS = [
  "#7db7ed",
  "#f4a460",
  "#90ee90",
  "#dda0dd",
  "#f08080",
  "#20b2aa",
  "#ffb347",
  "#87ceeb",
  "#deb887",
  "#98fb98",
  "#ff6b6b",
  "#4ecdc4",
  "#45b7d1",
  "#96ceb4",
  "#ffeaa7",
];

const renderLabel = ({ name, y, cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.04) {
    return null;
  }
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const ly = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={ly} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11}>
      {`${name} (${y})`}
    </text>
  );
};

export const PieView = forwardRef<PieRef, Props>(({
  locationData,
  sectionData,
  location,
  onChangeFilter,
}, ref) => {
  useImperativeHandle(ref, () => ({
    updatePieChart: () => {},
  }));

  const customTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
      return null;
    }
    const item = payload[0];
    const entry: PieItem = item.payload;
    const pct = ((item.percent ?? 0) * 100).toFixed(1);
    return (
      <div style={{ backgroundColor: '#fff', border: '2px solid #778844', padding: '0 10px', borderRadius: 5, color: '#000' }}>
        {entry.zone_id !== undefined && <p>ID: {entry.zone_id}</p>}
        <p>#: {entry.y}</p>
        <p>%: {pct}%</p>
      </div>
    );
  };

  const handleSectionClick = useCallback((data: PieItem) => {
    onChangeFilter(data.name, 'section_id');
  }, [onChangeFilter]);

  const handleLocationClick = useCallback((data: PieItem) => {
    onChangeFilter(data.name, 'location');
  }, [onChangeFilter]);

  return (
    locationData.length > 0 && sectionData.length > 0
      ? <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={sectionData as PieItem[]}
              dataKey="y"
              nameKey="name"
              outerRadius="80%"
              innerRadius="45%"
              labelLine={false}
              label={renderLabel}
              onClick={handleSectionClick}
              cursor="pointer"
            >
              {(sectionData as PieItem[]).map((_, index) => (
                <Cell key={`section-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            {!location && (
              <Pie
                data={locationData as PieItem[]}
                dataKey="y"
                nameKey="name"
                outerRadius="40%"
                innerRadius={0}
                labelLine={false}
                label={renderLabel}
                onClick={handleLocationClick}
                cursor="pointer"
              >
                {(locationData as PieItem[]).map((_, index) => (
                  <Cell key={`location-${index}`} fill={COLORS[(index + 5) % COLORS.length]} />
                ))}
              </Pie>
            )}
            <Tooltip content={customTooltip} />
          </PieChart>
        </ResponsiveContainer>
      : <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          No data
        </Box>
  );
});
